import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articleContent } from './articleData';

const MAIN_SITE_URL = 'https://kaspreadsheet.com';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();

  const lang = i18n.language === 'pt-br' ? 'pt-br' : i18n.language === 'de' ? 'de' : 'en';
  const langContent = articleContent[lang] || articleContent['en'];
  const article = langContent[slug || ''] || langContent['cssbuy-spreadsheet-guide'] || Object.values(langContent)[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [article]);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Navbar for Article */}
      <nav className="bg-white border-b border-background-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="text-lg font-bold text-primary-600 whitespace-nowrap">
              {t('nav_brand')}
            </a>
            <div className="flex items-center gap-4 md:gap-6">
              <a
                href="/"
                className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                {t('nav_home')}
              </a>
              <a
                href="/article/cssbuy-buying-guide"
                className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                {t('nav_shopping_guide')}
              </a>
              <a
                href="/"
                className="hidden sm:inline-flex text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                {t('article_back_home')}
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-background-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm">
            <a href="/" className="text-foreground-500 hover:text-primary-600 transition-colors">
              {t('article_breadcrumb_home')}
            </a>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />
            <span className="text-foreground-900 font-medium">{t('article_breadcrumb_article')}</span>
          </nav>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
            <span className="px-2 py-1 bg-primary-50 text-primary-600 rounded-full font-medium">Guide</span>
            <span>{article.lastUpdated}</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-6">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
            <span>{t('article_written_by')} <span className="font-medium text-foreground-700">{article.author}</span></span>
            <span className="hidden sm:inline">|</span>
            <span>{t('article_last_updated')}: {article.lastUpdated}</span>
          </div>
        </header>

        {/* Article body with HTML content */}
        <div
          className="prose prose-lg max-w-none prose-headings:text-foreground-900 prose-headings:font-bold prose-p:text-foreground-700 prose-p:leading-relaxed prose-a:text-primary-600 prose-a:hover:text-primary-700 prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-2 prose-ul:text-foreground-700 prose-strong:text-foreground-900 prose-strong:font-semibold"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* Related Articles / Internal Links */}
        <div className="mt-16 pt-8 border-t border-background-200">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">
            {t('article_related_articles')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a href="/" className="p-4 rounded-xl bg-white border border-background-200 hover:border-primary-200 hover:bg-primary-50 transition-all">
              <div className="text-sm font-semibold text-primary-600 mb-1">CSSBuy Spreadsheet</div>
              <div className="text-sm text-foreground-700">{t('hero_subtitle').substring(0, 80)}...</div>
            </a>
            <a href="/article/cssbuy-products" className="p-4 rounded-xl bg-white border border-background-200 hover:border-primary-200 hover:bg-primary-50 transition-all">
              <div className="text-sm font-semibold text-primary-600 mb-1">CSSBuy Products</div>
              <div className="text-sm text-foreground-700">{t('articles_seo_desc')}</div>
            </a>
            <a href="/article/cssbuy-sellers" className="p-4 rounded-xl bg-white border border-background-200 hover:border-primary-200 hover:bg-primary-50 transition-all">
              <div className="text-sm font-semibold text-primary-600 mb-1">CSSBuy Sellers</div>
              <div className="text-sm text-foreground-700">{t('articles_seo_desc')}</div>
            </a>
            <a href="/article/cssbuy-qc-guide" className="p-4 rounded-xl bg-white border border-background-200 hover:border-primary-200 hover:bg-primary-50 transition-all">
              <div className="text-sm font-semibold text-primary-600 mb-1">CSSBuy QC Guide</div>
              <div className="text-sm text-foreground-700">{t('articles_seo_desc')}</div>
            </a>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-12 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-semibold text-sm transition-all"
          >
            <i className="ri-arrow-left-line" />
            {t('article_back_home')}
          </a>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-foreground-900 text-white/80 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-bold text-white mb-2">{t('nav_brand')}</p>
          <p className="text-xs text-white/50">{t('footer_independent')}</p>
        </div>
      </footer>
    </div>
  );
}